import {
  COMPACT_NUMBER_DECIMAL_THRESHOLD,
  COMPACT_NUMBER_K_THRESHOLD,
  COMPACT_NUMBER_M_THRESHOLD,
} from '@/helpers/unit-formatter/shared/constants';
import { FormattedUnitLabels, NumericFormatterInput } from '@/helpers/unit-formatter/shared/types';
import { unitValueToFormattedUnitLabels } from '@/helpers/unit-formatter/shared/utils';

/**
 * Formats a numeric value with K/M suffixes for compact display.
 * Used by chart axis labels to save horizontal space while maintaining readability.
 */
export function formatCompactNumberValue(value: number): string {
  const absValue = Math.abs(value);

  if (absValue >= COMPACT_NUMBER_M_THRESHOLD) {
    const formatted = (value / COMPACT_NUMBER_M_THRESHOLD).toFixed(1);
    return `${formatted.replace('.0', '')}M`;
  }

  if (absValue >= COMPACT_NUMBER_K_THRESHOLD) {
    const formatted = (value / COMPACT_NUMBER_K_THRESHOLD).toFixed(1);
    return `${formatted.replace('.0', '')}K`;
  }

  if (absValue < COMPACT_NUMBER_DECIMAL_THRESHOLD) {
    return value.toFixed(1);
  }

  return Math.round(value).toString();
}

/**
 * Formatter for compact numeric display (K/M suffixes, axis-friendly precision).
 * Does not display engineering units — only the compact value string.
 *
 * Non-numeric passthrough is handled by `asPassthroughNonNumericFormatter` at registry build.
 */
export const formatCompactNumber = ({ value }: NumericFormatterInput): FormattedUnitLabels => {
  return unitValueToFormattedUnitLabels({
    value: formatCompactNumberValue(value),
    units: undefined,
  });
};
